import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { GetFileInfoProps } from 'api/file/file-api';
import { RouteType } from 'core/routes/Router';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { useViewState } from 'hooks/ViewStateContext';
import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';

export const MenuContext = createContext({
  menuCollapsed: false,
  setMenuCollapsed: (_collapsed: boolean) => {
    /* intentionally empty */
  },
});

export const useMenuContext = () => useContext(MenuContext);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentContainer: {
      paddingBottom: theme.spacing(8),
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    navContainer: {
      display: 'flex',
    },
    mainContainer: {
      width: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
    },
    contentWrapper: {
      overflow: 'auto',
      paddingBottom: theme.spacing(8),
      transition: 'all 0.3s ease',
      flexGrow: 1,
    },
    fileSnapshotContainer: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    navigationContainer: {
      display: 'flex',
      width: '100%',
      marginBottom: theme.spacing(2),
    },
  }),
);

interface ReportingLayoutProps {
  children: ReactNode;
  routes?: RouteType[];
}

const ReportingLayout: FC<ReportingLayoutProps> = ({ children, routes }) => {
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const classes = useStyles();
  const { fileId } = useParams<{ fileId: string }>();
  const { setRouterParamValue } = useViewState();

  // Get FileData and FincenReport contexts
  const fileDataContext = useContext(FileDataContext) as FileDataContextInterface;
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { fincenReport, fincenReportStatus, loadFincenReport, currentFileId } = fincenReportCtx ?? {};

  // Set routerParamValue from URL params on mount/refresh for LeftNav to use
  useEffect(() => {
    if (fileId) {
      setRouterParamValue(fileId);
    }
  }, [fileId, setRouterParamValue]);

  // Load file data when lost on refresh
  useEffect(() => {
    if (fileId && fileDataContext && !fileDataContext.fileData?.fileId && fileDataContext.loadFileData) {
      const fileProps: GetFileInfoProps = {
        fileId,
      };
      fileDataContext.loadFileData(fileProps);
    }
  }, [fileId, fileDataContext]);

  // Load fincen report when lost on refresh or when fileId changes
  useEffect(() => {
    if (fileId && (!fincenReport || fincenReportStatus === 'idle' || currentFileId !== fileId)) {
      loadFincenReport?.(fileId).catch(() => void 0);
    }
  }, [fileId, fincenReport, fincenReportStatus, currentFileId, loadFincenReport]);

  return (
    <MenuContext.Provider value={{ menuCollapsed, setMenuCollapsed }}>
      <div data-testid="fincen-reporting-layout" className={classes.mainContainer}>
        <div className={classes.contentWrapper}>
          <div className={classes.contentContainer}>
            {routes && routes.length > 0 ? (
              <Switch>
                {routes.map((route, i) => (
                  <Route
                    key={i}
                    path={route.path}
                    exact={route.exact}
                    render={(props) => <route.component {...props} routes={route.routes} />}
                  />
                ))}
                <Route
                  path="/files/:fileId/fincen-reporting"
                  exact
                  render={({ match }) => (
                    <Redirect to={`/files/${match.params.fileId}/fincen-reporting/filing-information`} />
                  )}
                />
              </Switch>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </MenuContext.Provider>
  );
};

export default ReportingLayout;
